import {
  Alert,
  Box,
  Button,
  MenuItem,
  Select,
  TextareaAutosize,
  Typography,
  styled
} from '@mui/material';

import {
  CancelDeleteUserButton,
  DeleteUserButton
} from 'src/components/Button';
import { RequiredField } from 'src/content/auth/signup';
import { REASON_LIST } from 'src/utils/constantTypes';

import { FinalConfirmationStepProps } from './types';

const Textarea = styled(TextareaAutosize)(({ theme }) => ({
  borderRadius: theme.general.borderRadiusLg,
  padding: theme.spacing(2),
  border: '1px solid lightgrey',
  boxSizing: 'border-box',
  width: '100%',
  resize: 'vertical',
  '&:focus': {
    outline: 'none',
    borderColor: theme.bg.primary,
    borderWidth: '2px'
  }
}));

const SelectReason = styled(Select)(({ theme }) => ({
  borderRadius: theme.general.borderRadiusLg,
  width: '100%',
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.bg.primary,
    borderWidth: '2px'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.bg.primary,
    borderWidth: '2px'
  }
}));

const FinalConfirmationStep = ({
  cancelSubscriptionObj,
  onReasonChange,
  onCommentChange,
  onBack,
  onPauseInstead,
  onConfirmCancel,
  isLoading,
  subscriptionEndDate
}: FinalConfirmationStepProps) => (
  <Box>
    <Box mb={3}>
      <Typography variant="h3" mb={1}>
        Are you sure you want to cancel?
      </Typography>
      <Typography color="text.secondary" variant="body1">
        This is the final step before your subscription is canceled.
      </Typography>
    </Box>

    <Alert icon={false} severity="warning" sx={{ alignItems: 'center', mb: 2 }}>
      <Typography variant="body2">
        Your subscription remains active until{' '}
        <Box component="span" fontWeight={700}>
          {subscriptionEndDate}
        </Box>
        . After that, app access will end, your practice location will be
        deactivated, and you won’t be charged again.
      </Typography>
    </Alert>

    <Box mb={3}>
      <Typography color="text.secondary" variant="body2">
        If you want to keep your account and data intact, you can choose to
        pause instead.
      </Typography>
      <Box mt={1}>
        <Button
          disabled={isLoading}
          onClick={onPauseInstead}
          sx={{ px: 0 }}
          variant="text"
        >
          Pause instead
        </Button>
      </Box>
    </Box>

    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Box component="label" display="block" fontWeight={700} mb={1}>
          Reason
          <RequiredField>*</RequiredField>
        </Box>
        <SelectReason
          displayEmpty
          onChange={(event) => onReasonChange(event.target.value as string)}
          renderValue={(selected) => {
            if (!selected) {
              return 'Select one..';
            }

            const selectedReason = REASON_LIST.find(
              (reason) => reason.key === selected
            );
            return selectedReason?.value || 'Select one..';
          }}
          size="small"
          value={cancelSubscriptionObj.reason}
        >
          <MenuItem disabled value="">
            Select one..
          </MenuItem>
          {REASON_LIST.map((reason) => (
            <MenuItem key={reason.key} value={reason.key}>
              {reason.value}
            </MenuItem>
          ))}
        </SelectReason>
      </Box>

      <Box>
        <Box component="label" display="block" fontWeight={700} mb={1}>
          Add Details
          <RequiredField>*</RequiredField>
        </Box>
        <Textarea
          aria-label="cancel-subscription-comment"
          minRows={6}
          onChange={(event) => onCommentChange(event.target.value)}
          placeholder="Add description here.."
          value={cancelSubscriptionObj.comment}
        />
      </Box>
    </Box>

    <Box
      display="flex"
      flexDirection={{ xs: 'column', sm: 'row' }}
      gap={2}
      mt={3}
    >
      <CancelDeleteUserButton disabled={isLoading} fullWidth onClick={onBack}>
        Go Back
      </CancelDeleteUserButton>
      <DeleteUserButton
        disabled={
          isLoading ||
          !cancelSubscriptionObj.reason ||
          !cancelSubscriptionObj.comment.trim()
        }
        fullWidth
        onClick={onConfirmCancel}
      >
        Confirm Cancellation
      </DeleteUserButton>
    </Box>
  </Box>
);

export default FinalConfirmationStep;
